/**
 * API utility with automatic token refresh
 */

import { resolveWebSocketUrl, isJwtExpired } from '@euroasiann/shared';
export { resolveWebSocketUrl, isJwtExpired };

export const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:3000');

function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  const normalized = contentType.toLowerCase();
  return normalized.includes('application/json') || normalized.includes('+json');
}

export async function parseResponseJson(response: Response): Promise<{
  data: unknown;
  text: string;
  parseError?: string;
}> {
  const text = (await response.text()).trim();

  if (!text) {
    return {
      data: null,
      text: '',
      parseError: `Empty response body (HTTP ${response.status})`,
    };
  }

  const contentType = response.headers.get('content-type');
  const looksLikeJson = text.startsWith('{') || text.startsWith('[');

  if (!isJsonContentType(contentType) && !looksLikeJson) {
    return {
      data: null,
      text,
      parseError: `Non-JSON response (HTTP ${response.status})`,
    };
  }

  try {
    return { data: JSON.parse(text), text };
  } catch {
    return {
      data: null,
      text,
      parseError: `Invalid JSON response (HTTP ${response.status})`,
    };
  }
}

export function getApiErrorMessage(
  response: Response,
  parsed: { data: unknown; parseError?: string },
  fallback: string
): string {
  if (parsed.data && typeof parsed.data === 'object' && parsed.data !== null) {
    const record = parsed.data as Record<string, unknown>;
    if (typeof record.error === 'string' && record.error.trim()) {
      return record.error;
    }
    if (typeof record.message === 'string' && record.message.trim()) {
      return record.message;
    }
  }
  return parsed.parseError || `${fallback} (HTTP ${response.status})`;
}

export function isApiGatewayUnreachable(response: Response, message: string): boolean {
  return response.status === 502 || /not reachable on port 3000|ECONNREFUSED|ECONNRESET/i.test(message);
}

export function getRfqFetchErrorMessage(
  response: Response,
  parsed: { data: unknown; parseError?: string }
): string {
  const message = getApiErrorMessage(response, parsed, 'Failed to fetch RFQs');
  if (isApiGatewayUnreachable(response, message)) {
    return 'Unable to reach the API server. Start the backend (apps/api: npm run dev) and refresh.';
  }
  return message;
}

export function apiFetchUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
    return normalized;
  }
  const base = API_URL || 'http://localhost:3000';
  return `${base}${normalized}`;
}

/** Absolute URL for API-hosted paths or pass-through for Firebase/CDN https URLs */
export function resolveFileUrl(path: string | undefined): string {
  if (!path) return '';
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('blob:') ||
    path.startsWith('data:')
  ) {
    return path;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  // Dev without VITE_API_URL: hit local API so /uploads/* resolves (Vite only proxies /api by default)
  if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
    return `http://localhost:3000${normalized}`;
  }
  const baseUrl = String(API_URL || 'http://localhost:3000').replace(/\/$/, '');
  return `${baseUrl}${normalized}`;
}

/** Routes where 401 must not trigger a hard redirect (avoids reload loops on /login). */
function isPublicAuthRoute(): boolean {
  const path = window.location.pathname;
  return (
    path === '/login' ||
    path.startsWith('/set-password') ||
    path.startsWith('/change-password') ||
    path.startsWith('/forgot-password') ||
    path.startsWith('/reset-password') ||
    path.startsWith('/onboarding/')
  );
}

function redirectToLogin(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  if (!isPublicAuthRoute()) {
    window.location.href = '/login';
  }
}

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh the access token using the refresh token
 */
export async function refreshAccessToken(): Promise<string | null> {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return null;
      }

      let url: string;
      if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
        url = '/api/v1/auth/refresh';
      } else {
        url = API_URL ? `${API_URL}/api/v1/auth/refresh` : 'http://localhost:3000/api/v1/auth/refresh';
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      if (data.success && data.data?.accessToken) {
        localStorage.setItem('accessToken', data.data.accessToken);
        return data.data.accessToken as string;
      }

      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
      redirectToLogin();
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Get a valid (non-expired) access token, refreshing if necessary
 */
export async function getValidAccessToken(): Promise<string | null> {
  let token = localStorage.getItem('accessToken');
  if (!token || isJwtExpired(token)) {
    token = await refreshAccessToken();
  }
  return token;
}

/**
 * Make an authenticated API request with automatic token refresh
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  let token = await getValidAccessToken();

  // Build full URL
  let fullUrl: string;
  if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
    fullUrl = url.startsWith('/') ? url : `/${url}`;
  } else {
    fullUrl = API_URL ? `${API_URL}${url.startsWith('/') ? url : `/${url}`}` : `http://localhost:3000${url.startsWith('/') ? url : `/${url}`}`;
  }

  // Make initial request
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  // Only set Content-Type when sending a body (never on file/GET downloads)
  if (
    options.body &&
    !headers['Content-Type'] &&
    !(options.body instanceof FormData)
  ) {
    headers['Content-Type'] = 'application/json';
  }

  let response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // If token expired during request, try to refresh once more
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    
    if (newToken) {
      // Retry the request with new token
      const retryHeaders: Record<string, string> = {
        ...(options.headers as Record<string, string> || {}),
        Authorization: `Bearer ${newToken}`,
      };
      
      if (!retryHeaders['Content-Type'] && !(options.body instanceof FormData)) {
        retryHeaders['Content-Type'] = 'application/json';
      }

      response = await fetch(fullUrl, {
        ...options,
        headers: retryHeaders,
      });
    } else {
      redirectToLogin();
    }
  }

  return response;
}

